import { FC, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GETObjectWithDefaultCriteria } from 'src/types'
import { FormInput } from '@microservices/wiskey-react-components'
import { AutocompleteOption } from '@microservices/wiskey-react-components/dist/AutoComplete'
import { AutocompleteRenderGetTagProps, Chip } from '@mui/material'

import { SearchAssistantsCreateOrEditContext } from '@pages/SearchAssistantsCreateOrEdit'

import { useFetchAllCriteriaByObjectCodesQuery } from '@redux/api'

import { GENERATOR_INPUT_TYPE, SEARCH_FIELD_TYPE, WIDTH_DEFAULT_CRITERIAS_MODAL } from '@constants'

type ObjectInputProps = {
  object: GETObjectWithDefaultCriteria
  setError: (name: string, error: string) => void
  clearErrors: (name: string) => void
}

export const ObjectInput: FC<ObjectInputProps> = ({ object, setError, clearErrors }) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const { searchAssistantId } = useContext(SearchAssistantsCreateOrEditContext)

  const { data: criteriasData, isLoading } = useFetchAllCriteriaByObjectCodesQuery(
    {
      objectCodes: [object.objectCode],
      searchAssistantId: searchAssistantId ? searchAssistantId : '',
    },
    {
      skip: !object.defaultSearchFilters.length && !open,
      refetchOnMountOrArgChange: true,
    }
  )

  const criterias = criteriasData?.data

  const options = useMemo(
    () =>
      criterias?.map(criteria => ({
        id: criteria.id,
        label: criteria.title,
        haveRelativeValue: criteria.storedValues.some(
          value => value.fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE
        ),
      })) || [],
    [criterias]
  )

  const customRenderTags = (
    tagValue: AutocompleteOption[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => {
    const tags = tagValue.map(tag => {
      const option = options.find(option => tag.id === option.id)

      return {
        ...tag,
        haveRelativeValue: option?.haveRelativeValue,
      }
    })

    const isMoreThanOneRelative =
      tags.filter(({ haveRelativeValue }) => haveRelativeValue).length > 1

    return tags.map((option: AutocompleteOption, index: number) => {
      const isRelativeError = option.haveRelativeValue && isMoreThanOneRelative

      return (
        <Chip
          {...getTagProps({ index })}
          key={index}
          label={option.label}
          size='small'
          sx={
            isRelativeError
              ? { background: 'red', color: 'white' }
              : index > 4
              ? { background: '#cdcdcd' }
              : {}
          }
          title={
            isRelativeError
              ? t('searchAssistant.oneRelative')
              : t('searchAssistant.affects', {
                  entity: index > 4 ? 'DropDown' : 'Search Assistant',
                })
          }
        />
      )
    })
  }

  const onChangeAutocomplete = array => {
    const tags = options.filter(({ id }) => array.some(tag => tag.id === id))
    const isMoreThanOneRelative =
      tags.filter(({ haveRelativeValue }) => haveRelativeValue).length > 1
    if (isMoreThanOneRelative) {
      setError(object.objectCode, t('searchAssistant.oneRelative'))
    } else {
      clearErrors(object.objectCode)
    }
  }

  return (
    <FormInput
      multiple
      autocompleteOptions={options}
      disablePortal={true}
      inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
      label={object.objectTitle}
      labelSx={{ width: WIDTH_DEFAULT_CRITERIAS_MODAL / 2, pr: 2 }}
      loading={isLoading}
      name={object.objectCode}
      open={open}
      autoCompleteRenderTags={(
        tagValue: AutocompleteOption[],
        getTagProps: AutocompleteRenderGetTagProps
      ) => customRenderTags(tagValue, getTagProps)}
      onChangeAutocomplete={onChangeAutocomplete}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  )
}
